import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { GenericModal } from '@components/common/modals';
import Button from '@components/common/Button';
import { Checkbox } from '@components/common';
import { RZTable } from '@components/common/table';
import { TableColumn } from '@app-types';
import apiClient from '@services/apiClient';
import styles from './MicrosoftSyncModal.module.css';

interface UserSyncModalProps<T extends { id: string }> {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  syncUrl: string;
  getName: (user: T) => string;
  getEmail: (user: T) => string;
  onImport: (users: T[]) => Promise<void>;
  dataTestId?: string;
}

function UserSyncModal<T extends { id: string }>({
  isOpen,
  onClose,
  title,
  syncUrl,
  getName,
  getEmail,
  onImport,
  dataTestId = 'user-sync'
}: UserSyncModalProps<T>) {
  const [users, setUsers] = useState<T[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    } else {
      setUsers([]);
      setSelectedUsers(new Set());
      setSearchTerm('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<{ data: T[] }>(`${syncUrl}?search=${searchTerm}`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to sync users', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleSelect = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = useCallback(() => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map((u) => u.id)));
    }
  }, [selectedUsers.size, users]);

  const handleImport = async () => {
    if (selectedUsers.size === 0) return;
    setIsImporting(true);
    const usersToImport = users.filter((u) => selectedUsers.has(u.id));
    try {
      await onImport(usersToImport);
      onClose();
    } catch (error) {
      console.error('Import failed', error);
    } finally {
      setIsImporting(false);
    }
  };

  const columns: TableColumn[] = useMemo(
    () => [
      {
        key: 'checkbox',
        label: (
          <Checkbox checked={users.length > 0 && selectedUsers.size === users.length} onChange={handleSelectAll} />
        ),
        defaultVisible: true,
        sortable: false,
        alwaysVisible: true,
        centered: true
      },
      { key: 'name', label: 'Name', defaultVisible: true, sortable: false, centered: true },
      { key: 'email', label: 'Email', defaultVisible: true, sortable: false, centered: true }
    ],
    [users.length, selectedUsers.size, handleSelectAll]
  );

  const renderCell = (column: TableColumn, user: T) => {
    switch (column.key) {
      case 'checkbox':
        return (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              checked={selectedUsers.has(user.id)}
              onChange={() => handleToggleSelect(user.id)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      case 'name':
        return <div style={{ textAlign: 'center' }}>{getName(user)}</div>;
      case 'email':
        return <div style={{ textAlign: 'center' }}>{getEmail(user)}</div>;
      default:
        return null;
    }
  };

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} title={title} size="lg" dataTestId={dataTestId}>
      <div className={styles.modal} data-test-id={`${dataTestId}-content`}>
        <div className={styles.controls}>
          <div className={styles.search}>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
              className={styles.searchInput}
            />
            <Button variant="secondary" onClick={fetchUsers} loading={isLoading} size="sm">
              Search
            </Button>
          </div>
        </div>

        <RZTable
          data={users}
          columns={columns}
          getRowKey={(user) => user.id}
          renderCell={renderCell}
          onRowClick={(user) => handleToggleSelect(user.id)}
          state={{
            isLoading,
            isEmpty: users.length === 0,
            emptyTitle: 'No new users found',
            emptyDescription: 'No users available to import.',
            emptyType: 'default',
            loadingMessage: 'Loading users...'
          }}
        />

        <div className={styles.footer}>
          <span className={styles.selectionCount}>
            {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
          </span>
          <div className={styles.actions}>
            <Button variant="secondary" onClick={onClose} disabled={isImporting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={selectedUsers.size === 0 || isImporting}
              loading={isImporting}
            >
              Import Selected
            </Button>
          </div>
        </div>
      </div>
    </GenericModal>
  );
}

export default UserSyncModal;
